/**
 * Santa Gota (rendimiento) - videos de los slides que no se ven al entrar.
 *
 * Por que existe: el marcado que emite `video_tag` lleva `autoplay`, y `autoplay`
 * anula el `preload="metadata"` que viene a su lado. Con tres slides de video en la
 * portada el navegador se bajaba los tres enteros -16,4 MB medidos en un iPhone-
 * aunque dos no se lleguen a ver nunca. blocks/_slide.liquid deja los slides que van
 * despues del primero sin `src` y sin `autoplay`; este archivo se los devuelve en el
 * momento en que hacen falta.
 *
 * Por que cargar y reproducir van separados: Chrome pausa por su cuenta cualquier
 * video que se reproduzca fuera de pantalla, y aqui ya no queda atributo `autoplay`
 * que lo reanude. Un unico play() al activar el slide dejaria el video congelado
 * justo cuando aparece. Por eso reproducir() se vuelve a llamar cada vez que el
 * slide entra en pantalla, y la precarga en reposo se limita a cargar().
 *
 * Que copia se baja: en pantallas de menos de 750 px se pide la de 720p en vez de la
 * de 1080p. No es bajar calidad: un telefono de 390 px a DPR3 tiene 1170 pixeles
 * reales y la copia de 720p trae 1280, asi que la de 1080p no aporta detalle, solo
 * peso. El primer slide no pasa por aqui y conserva su 1080p siempre.
 *
 * Se puede borrar sin miedo: si este archivo no llega, los slides que van despues
 * del primero se quedan con su foto de portada -la misma que ya esta debajo del
 * video, y que es justo su primer fotograma- y no cambia nada mas del tema.
 */

if (!window.santaGotaSlideVideoDefer) {
  window.santaGotaSlideVideoDefer = true;

  /** Marca que pone blocks/_slide.liquid en lugar del `src` del `<source>`. */
  const FUENTE_PENDIENTE = 'source[data-slide-video-src]';

  /**
   * Elige entre la copia de 720p y la de 1080p segun el ancho de la ventana.
   *
   * El corte son los mismos 750 px que usa el resto del tema para separar telefono de
   * escritorio. La razon de fondo no es el ancho en si sino los pixeles reales de la
   * pantalla: un telefono de 390 px a DPR3 tiene 1170, y la copia de 720p trae 1280.
   * Mandarle la de 1080p no le da un pixel mas de detalle, solo mas megas.
   *
   * Se decide una sola vez, al cargar el video, y no se vuelve a mirar: cambiar la
   * fuente a media reproduccion reiniciaria el video, que si se nota.
   * @param {HTMLSourceElement} fuente
   */
  const direccionSegunPantalla = (fuente) => {
    const liviana = fuente.dataset.slideVideoSrcMovil;
    const completa = fuente.dataset.slideVideoSrc;

    if (liviana && window.matchMedia('(max-width: 749px)').matches) return liviana;
    return completa;
  };

  /** Videos a los que ya se les devolvio el `src`, para no repetir el load(). */
  const cargados = new WeakSet();

  /** Videos que ya vigila el observador, para no observarlos dos veces. */
  const observados = new WeakSet();

  /**
   * Devuelve el `src` al video y lo baja. Idempotente: un segundo load() reiniciaria
   * la descarga y devolveria el video al primer fotograma.
   * @param {HTMLVideoElement | null} video
   */
  const cargar = (video) => {
    if (!video || cargados.has(video)) return;

    const fuentes = video.querySelectorAll(FUENTE_PENDIENTE);
    if (fuentes.length === 0) return;

    cargados.add(video);

    for (const fuente of fuentes) {
      const url = direccionSegunPantalla(fuente);
      if (!url) continue;
      fuente.setAttribute('src', url);
      fuente.removeAttribute('data-slide-video-src');
      fuente.removeAttribute('data-slide-video-src-movil');
    }

    // El marcado sale con preload="none" para que nada baje antes de tiempo. A partir
    // de aqui si lo queremos entero: es el momento en que el slide esta por verse.
    video.preload = 'auto';
    video.load();
  };

  /**
   * Pone el video a andar. Se puede llamar tantas veces como haga falta: sobre un
   * video que ya corre no hace nada, y sobre uno que Chrome pauso por estar fuera de
   * pantalla lo reanuda.
   * @param {HTMLVideoElement | null} video
   */
  const reproducir = (video) => {
    if (!video) return;

    cargar(video);
    if (!cargados.has(video)) return;

    const intento = video.play();
    // play() puede rechazar si el navegador todavia no quiere reproducir. No es un
    // error nuestro y no hay nada que hacer: la foto de portada sigue puesta.
    if (intento && typeof intento.catch === 'function') intento.catch(() => {});
  };

  /**
   * El slideshow es un carrusel que se desplaza de lado, y el slide de al lado asoma
   * por una fraccion de pixel: medido, el observador daba por visible el slide 2
   * mientras se veia el 1. Por eso no basta con que el video toque la pantalla, tiene
   * que ser ademas el slide que el slideshow da por activo. El propio tema marca cual
   * es con aria-hidden, asi que se le pregunta a el en vez de inventar un umbral.
   * @param {Element} video
   */
  const esElSlideActivo = (video) => {
    const slide = video.closest('slideshow-slide');
    return !slide || slide.getAttribute('aria-hidden') !== 'true';
  };

  /**
   * Si el slideshow no esta a la vista no hay nada que encender: el carrusel sigue
   * girando solo aunque el visitante ya haya bajado, y sin esta pregunta cada giro se
   * traia un video de 6 MB que nadie iba a ver. Cuando el visitante vuelve a subir se
   * encarga el observador. El atributo `in-viewport` lo pone assets/slideshow.js.
   * @param {Element} nodo
   */
  const estaALaVista = (nodo) => {
    if (document.visibilityState === 'hidden') return false;

    const slideshow = nodo.closest('slideshow-component');
    return !slideshow || slideshow.hasAttribute('in-viewport');
  };

  /**
   * Red de seguridad: el video se enciende cuando su slide esta de verdad en pantalla,
   * llegue o no el evento del slideshow. No se deja de observar nunca, porque cada vez
   * que el slide vuelve a entrar hay que reanudar: Chrome pausa lo que no se ve.
   */
  const observador =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entradas) => {
            for (const entrada of entradas) {
              const video = /** @type {HTMLVideoElement} */ (entrada.target);
              if (entrada.isIntersecting && esElSlideActivo(video)) reproducir(video);
            }
          },
          // Con un solo umbral en 0 el aviso no vuelve si el slide nunca llego a salir
          // del todo de la pantalla; el de 0,5 asegura que llegue al ponerse delante.
          { threshold: [0, 0.5] }
        )
      : null;

  /** Apunta los videos que aun estan pendientes. Se puede llamar de nuevo sin coste. */
  const registrar = () => {
    if (!observador) return;

    for (const fuente of document.querySelectorAll(FUENTE_PENDIENTE)) {
      const video = fuente.closest('video');
      if (!video || observados.has(video)) continue;
      observados.add(video);
      observador.observe(video);
    }
  };

  /**
   * Adelanta el primer video que sigue sin `src`, en el orden en que estan los slides.
   *
   * Se le pasa el slideshow que hizo el cambio y no el documento entero, porque la
   * barra de anuncios tambien es un slideshow y tambien avisa: sin acotar, sus avisos
   * se traian los videos de la portada antes de tiempo.
   *
   * Y no se adelanta nada si el slideshow no esta a la vista: a quien pasa de largo o
   * se va antes de los seis segundos no hay por que bajarle un video que no va a ver.
   * El atributo `in-viewport` lo pone el propio tema desde assets/slideshow.js.
   * @param {ParentNode} [raiz]
   */
  const precargarSiguiente = (raiz) => {
    const fuente = (raiz || document).querySelector(FUENTE_PENDIENTE);
    if (!fuente) return;

    const video = fuente.closest('video');
    if (video && estaALaVista(video)) cargar(video);
  };

  const enReposo = (tarea) => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(tarea, { timeout: 3000 });
    } else {
      setTimeout(tarea, 1200);
    }
  };

  // El slideshow de la portada gira solo cada 6 segundos, asi que el video del slide
  // siguiente tiene que estar listo antes de que le toque: se baja en cuanto la pagina
  // termina de cargar y el hilo queda libre, nunca compitiendo con el primer pintado.
  // Con "ahorro de datos" activado no se adelanta nada: cada video espera a su slide.
  const ahorroDeDatos = Boolean(navigator.connection && navigator.connection.saveData);

  const alTerminarLaCarga = (tarea) => {
    if (document.readyState === 'complete') enReposo(tarea);
    else window.addEventListener('load', () => enReposo(tarea), { once: true });
  };

  // Al cambiar de slide: el que llega se enciende, y el siguiente se va bajando.
  // El nombre del evento y el `slide` del detalle salen de assets/events.js
  // (SlideshowSelectEvent), que assets/slideshow.js emite en sus tres caminos:
  // al elegir un slide, al arrastrarlo y al desplazarse.
  document.addEventListener('slideshow:select', (evento) => {
    const slide = evento.detail && evento.detail.slide;
    if (!slide || !estaALaVista(slide)) return;

    registrar();
    reproducir(slide.querySelector('video.slide__video'));

    const slideshow = slide.closest('slideshow-component');
    if (!ahorroDeDatos && slideshow) enReposo(() => precargarSiguiente(slideshow));
  });

  registrar();
  if (!ahorroDeDatos) alTerminarLaCarga(() => precargarSiguiente(document));
}
